/**
 * TargetLanguagesSection
 *
 * Multi-select language picker (no upper limit).
 * When outputMode === 'hard_burn': enforced to single-select (maxSelection=1).
 * Reuses NLLBLanguageSelector.
 */

import { memo, useCallback } from 'react';
import { Globe } from 'lucide-react';
import { NLLBLanguageSelector } from '@/components/common/LanguageSelector';
import { useTranslation } from '@/hooks/useTranslation';
import { useSubtitles } from '../SubtitlesContext.context';

export const TargetLanguagesSection = memo(function TargetLanguagesSection() {
  const { t } = useTranslation();
  const { state, isUploading, isGenerating, handleTargetLanguagesChange } = useSubtitles();
  const { targetLanguages, outputMode } = state;

  const isHardBurn = outputMode === 'hard_burn';

  // In hard_burn mode: enforce single language at the handler level
  const handleChange = useCallback(
    (languages: string[]) => {
      if (isHardBurn && languages.length > 1) {
        // Keep only the most recently selected (last element)
        handleTargetLanguagesChange([languages[languages.length - 1]]);
      } else {
        handleTargetLanguagesChange(languages);
      }
    },
    [isHardBurn, handleTargetLanguagesChange]
  );

  return (
    <div className="panel-glass">
      <h3 className="text-base sm:text-lg font-semibold text-white mb-1 flex items-center space-x-2">
        <Globe className="h-4 w-4 sm:h-5 sm:w-5 text-purple-400" />
        <span>{t('subtitles.targetLanguages.title', { defaultValue: 'Target Languages' })}</span>
      </h3>
      <p className="text-sm text-white/60 mb-3">
        {isHardBurn
          ? t('subtitles.targetLanguages.hardBurnDescription', {
              defaultValue:
                'Hard burn-in supports exactly one language per render. Submit separate jobs for additional languages.',
            })
          : t('subtitles.targetLanguages.description', {
              defaultValue: 'Choose one or more languages for your subtitle files. No limit.',
            })}
        {targetLanguages.length > 0 && !isHardBurn && (
          <span className="ml-2 text-purple-400 font-medium">
            {targetLanguages.length}{' '}
            {t('subtitles.targetLanguages.selected', { defaultValue: 'selected' })}
          </span>
        )}
      </p>

      {isHardBurn && (
        <p className="text-xs text-amber-400/80 mb-3 bg-amber-900/10 border border-amber-500/20 rounded p-2">
          {t('subtitles.hardBurn.singleLanguageNote', {
            defaultValue:
              'One language per render. Each language requires a separate job with its own GPU encoding pass.',
          })}
        </p>
      )}

      <NLLBLanguageSelector
        values={targetLanguages}
        onMultiChange={handleChange}
        disabled={isUploading || isGenerating}
        showQuickActions={!isHardBurn}
        placeholder={
          isHardBurn
            ? t('subtitles.targetLanguages.hardBurnPlaceholder', {
                defaultValue: 'Select one language...',
              })
            : t('subtitles.targetLanguages.placeholder', {
                defaultValue: 'Select target languages...',
              })
        }
      />
    </div>
  );
});
